import { collection, doc, addDoc, query, where, orderBy, limit, getDocs, updateDoc, serverTimestamp, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface Notification {
  id: string;
  userId: string;
  type: 'like' | 'comment' | 'follow' | 'share' | 'subscription' | 'system';
  title: string;
  message: string;
  read: boolean;
  createdAt: Timestamp | Date;
  link?: string;
  metadata?: Record<string, any>;
}

export class NotificationService {
  static async createNotification(
    userId: string,
    type: Notification['type'],
    title: string,
    message: string,
    link?: string,
    metadata?: Record<string, any>
  ): Promise<string> {
    try {
      const notificationRef = await addDoc(collection(db, 'notifications'), {
        userId,
        type,
        title,
        message,
        read: false,
        link,
        metadata,
        createdAt: serverTimestamp()
      });
      return notificationRef.id;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  static async getNotifications(userId: string, limitCount: number = 20): Promise<Notification[]> {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt || new Date()
      })) as Notification[];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  static async markAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, { read: true });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  static async markAllAsRead(userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );
      const snapshot = await getDocs(q);
      const updates = snapshot.docs.map(docRef => 
        updateDoc(doc(db, 'notifications', docRef.id), { read: true })
      );
      await Promise.all(updates);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  static async getUnreadCount(userId: string): Promise<number> {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  static subscribeToNotifications(
    userId: string,
    callback: (notifications: Notification[]) => void,
    limitCount: number = 20
  ): () => void {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      return onSnapshot(q, (snapshot) => {
        const notifications = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || new Date()
        })) as Notification[];
        callback(notifications);
      }, (error) => {
        console.error('Error in notification subscription:', error);
      });
    } catch (error) {
      console.error('Error setting up notification subscription:', error);
      return () => {};
    }
  }

  static subscribeToUnreadCount(
    userId: string,
    callback: (count: number) => void
  ): () => void {
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false)
      );
      
      return onSnapshot(q, (snapshot) => {
        callback(snapshot.size);
      }, (error) => {
        console.error('Error in unread count subscription:', error);
      });
    } catch (error) {
      console.error('Error setting up unread count subscription:', error);
      return () => {};
    }
  }
}

